import { SerializedStyles } from "@emotion/react";
import { Ref, memo, useCallback } from "react";
import { useMaybeSubmit } from "src/apps/AppFlow/pages/hooks";
import { styles as GLOBAL_STYLES } from "src/apps/AppFlow/pages/styles";
import { DropdownSelector, FormFlowPage, RadioGroup, TextInput } from "src/components";
import RadioButton from "src/components/RadioButton";
import { createStyles } from "src/styles";
import { MortgageRateAndTypeValue } from "src/types/Application";
import { noInput } from "src/utilities/input";

import { enums, formatters, verify } from "@fraction/shared";

const { DEBT_TYPE_MAP } = formatters.enums;

export interface MortgageRateAndTermInformationProps {
  onChange: (value: MortgageRateAndTypeValue) => void;
  value?: MortgageRateAndTypeValue;
  propertyAddress?: string;
  lenderName: string;
  isSubpage?: boolean;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const styles = createStyles({
  radio: {
    marginTop: 24,
    marginBottom: 40,
  },
  button: { marginRight: 40 },
});

const MortgageRateAndType = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  propertyAddress,
  lenderName,
  isSubpage = false,
  onChange,
  ...props
}: MortgageRateAndTermInformationProps) => {
  const validate = useCallback(
    () => !noInput(value?.currentRate) && !noInput(value?.mortgageType) && !noInput(value?.rateType),
    [value]
  );
  const { handleNext, nextDisabled } = useMaybeSubmit({ onNext, validate, shouldSubmit: !isSubpage });

  const handleChangeCurrentRate = useCallback(
    (currentRate?: string) => {
      onChange?.({
        ...value,
        currentRate,
      });
    },
    [onChange, value]
  );

  const handleChangeRateType = useCallback(
    (rateType?: enums.RateType) => {
      onChange?.({
        ...value,
        rateType,
      });
    },
    [onChange, value]
  );

  const handleChangeMortgageType = useCallback(
    (mortgageType?: enums.DebtType) => {
      onChange?.({
        ...value,
        mortgageType,
      });
    },
    [onChange, value]
  );

  return (
    <FormFlowPage
      {...props}
      title={`${lenderName} mortgage rate & type`}
      description={`What is the current interest rate for your ${lenderName} mortgage on ${
        propertyAddress || "your property"
      }?`}
      onNext={handleNext}
      onBack={onBack}
      style={style}
      nextDisabled={nextDisabled}
    >
      <TextInput
        ref={focusRef}
        style={GLOBAL_STYLES.topInput}
        name="currentRate"
        inputMode="decimal"
        label="Current rate"
        placeholder="e.g. 3.49%"
        value={value?.currentRate || ""}
        onChange={handleChangeCurrentRate}
        maskOptions={{
          alias: "percentage",
          rightAlign: false,
          jitMasking: true,
          suffix: "%",
          digits: 2,
        }}
      />
      <DropdownSelector
        label="Mortgage type"
        placeholder="What sort of mortgage is it?"
        value={value?.mortgageType}
        onChange={handleChangeMortgageType}
        options={verify.mortgageTypes.map((type) => ({
          value: type,
          label: DEBT_TYPE_MAP[type],
        }))}
      />
      <RadioGroup style={styles.radio} value={value?.rateType} onChange={handleChangeRateType}>
        <RadioButton style={styles.button} label="Fixed" value={enums.RateType.FIXED} />
        <RadioButton label="Variable" value={enums.RateType.VARIABLE} />
      </RadioGroup>
    </FormFlowPage>
  );
};

export default memo(MortgageRateAndType);
